import React, { useCallback, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import AuthProfile from "../features/auth/services/model/AuthProfile";
import useAuthUserProfileApiService from "../features/auth/services/api/AuthUserProfileApiService";
import ProgressIndicator from "../themes/components/ProgressIndicator";
import NotAuthenticated from "../common/pages/NotAuthenticated";
import { useLogout } from "../hooks/useLogout";
import SessionTimeoutManager from "../utils/SessionTimeoutManager";

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

export const AuthContext = React.createContext<AuthProfile | undefined>(
  undefined
);
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { isError, isLoading, data } = useAuthUserProfileApiService();
  const logout = useLogout();
  const { pathname, hash, search } = useLocation();

  const inactivityTimerRef = React.useRef<NodeJS.Timeout>();

  const resetInactivityTimer = useCallback((): void => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      logout(true);
    }, INACTIVITY_TIMEOUT_MS);
  }, [logout]);

  const getRedirectUrl = useCallback((): string => {
    const hashPart = hash ? `#${hash}` : "";
    const searchPart = search ? `?${search}` : "";
    return `${pathname}${hashPart}${searchPart}`;
  }, [pathname, hash, search]);

  useEffect(() => {
    if (!data) return;

    // Check absolute timeout (24 hours max)
    if (SessionTimeoutManager.hasSessionExpired()) {
      logout(true);
      return;
    }

    const events = ["mousedown", "keydown", "touchstart", "focus"];

    const handleUserActivity = (): void => {
      resetInactivityTimer();
    };

    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = (): void => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        resetInactivityTimer();
      }, 250);
    };

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    window.addEventListener("scroll", handleScroll, { passive: true });

    resetInactivityTimer();

    // eslint-disable-next-line consistent-return
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      clearTimeout(scrollTimeout);
      events.forEach((event) =>
        window.removeEventListener(event, handleUserActivity)
      );
      window.removeEventListener("scroll", handleScroll);
    };
  }, [data, resetInactivityTimer, logout]);

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <NotAuthenticated />;
  }

  if (!data) {
    return (
      <Navigate replace to="/login" state={{ redirect: getRedirectUrl() }} />
    );
  }

  return <AuthContext.Provider value={data}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
